import { useMemo, useState } from 'react'
import { useTranslation } from '../../i18n'
import { COLORS } from '../../lib/theme'
import { useTeamRoleLabel } from '../../lib/roles'
import { Icon } from '../Icon'
import { PlanHuntAvatar } from './PlanHuntAvatar'
import { orderPlanHuntMembers } from './orderMembers'
import type { PlanHuntMember } from './types'
import { InviteByEmailOrPhoneModal, type InviteModalLabels } from '../InviteByEmailOrPhoneModal'

/**
 * A guest invited by email or phone from the wizard, before the hunt exists.
 * Structurally the domain's normalized `MemberInvite`, so the page can pass its
 * collected invites straight in without a conversion. Rendered as a pending row
 * here; the page sends them once finalize creates the hunt and its join code.
 */
export interface PlanHuntGuestInviteDraft {
  channel: 'email' | 'sms'
  value: string
}

export interface PlanHuntParticipantsStepProps {
  roster: PlanHuntMember[]
  leaderIds: string[]
  participantIds: string[]
  onChange: (next: string[]) => void
  currentUserId?: string
  /**
   * Guests collected to invite by email or phone. Omitting `onAddGuest` hides
   * the whole guest affordance, so the step renders exactly as before anywhere
   * the guest flow isn't wired. `onAddGuest` validates and stores the entry
   * (returning `{ ok:false, error }` to keep the modal open on a bad address);
   * the actual email/SMS is sent later, at finalize.
   */
  guests?: PlanHuntGuestInviteDraft[]
  onAddGuest?: (input: { email: string; phone: string; country: string }) => Promise<{ ok: boolean; error?: string }>
  onRemoveGuest?: (index: number) => void
}

export function PlanHuntParticipantsStep({
  roster,
  leaderIds,
  participantIds,
  onChange,
  currentUserId,
  guests,
  onAddGuest,
  onRemoveGuest,
}: PlanHuntParticipantsStepProps) {
  const { t } = useTranslation('planHunt')
  const roleLabel = useTeamRoleLabel()
  const [query, setQuery] = useState('')
  const [inviteOpen, setInviteOpen] = useState(false)
  const inviteLabels: InviteModalLabels = {
    title: t('participants.guest.modal.title'),
    subtitle: t('participants.guest.modal.subtitle'),
    email: t('participants.guest.modal.email'),
    orWith: t('participants.guest.modal.orWith'),
    country: t('participants.guest.modal.country'),
    number: t('participants.guest.modal.number'),
    add: t('participants.guest.modal.add'),
    sending: t('participants.guest.modal.sending'),
    cancel: t('participants.guest.modal.cancel'),
  }
  // Creator first, everyone else alphabetical by first name — same order as Leaders.
  const ordered = useMemo(() => orderPlanHuntMembers(roster, currentUserId), [roster, currentUserId])
  const filtered = useMemo(
    () => (query ? ordered.filter((m) => m.name.toLowerCase().includes(query.toLowerCase())) : ordered),
    [query, ordered],
  )
  const leaderSet = useMemo(() => new Set(leaderIds), [leaderIds])
  const selectedSet = useMemo(() => new Set(participantIds), [participantIds])

  const toggle = (id: string) => {
    if (leaderSet.has(id)) return
    const next = new Set(selectedSet)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    onChange(Array.from(next))
  }
  const selectAll = () => {
    const next = new Set(selectedSet)
    for (const m of filtered) next.add(m.id)
    onChange(Array.from(next))
  }
  const clear = () => onChange(leaderIds.slice())

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', flex: 1, minWidth: 220, maxWidth: 360 }}>
          <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: COLORS.outline }}>
            <Icon name="search" size={16} />
          </span>
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('participants.searchPlaceholder')}
            style={{
              width: '100%',
              padding: '9px 12px 9px 34px',
              fontSize: 13,
              border: `1.5px solid ${COLORS.outlineVariant}`,
              borderRadius: 999,
              outline: 'none',
              background: COLORS.surfaceContainerLowest,
              color: COLORS.onSurface,
            }}
          />
        </div>
        <div style={{ flex: 1 }} />
        <span style={{ fontSize: 12, color: COLORS.outline, fontWeight: 600 }}>
          {t('participants.selectedCount', { selected: participantIds.length, total: roster.length })}
        </span>
        <button
          type="button"
          className="wh-btn wh-btn-outline"
          style={{ height: 32, padding: '0 10px', fontSize: 12 }}
          onClick={selectAll}
        >
          {t('participants.selectAll')}
        </button>
        <button
          type="button"
          className="wh-btn wh-btn-outline"
          style={{ height: 32, padding: '0 10px', fontSize: 12 }}
          onClick={clear}
        >
          {t('participants.clear')}
        </button>
      </div>

      <div className="wh-card" style={{ overflow: 'hidden' }}>
        {filtered.map((m, i) => {
          const checked = selectedSet.has(m.id)
          const isLeader = leaderSet.has(m.id)
          const isMe = currentUserId != null && m.id === currentUserId
          return (
            <label
              key={m.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: '10px 14px',
                borderBottom: i === filtered.length - 1 ? 'none' : `1px solid ${COLORS.surfaceContainerHighest}`,
                cursor: isLeader ? 'default' : 'pointer',
                background: checked ? COLORS.primaryOpacity8 : COLORS.surfaceContainerLowest,
              }}
            >
              <PlanHuntAvatar member={m} size={34} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6 }}>
                  {m.name}
                  {isMe && <span style={{ fontSize: 11, fontWeight: 500, color: COLORS.outline }}>{t('participants.you')}</span>}
                  {isLeader && (
                    <span className="wh-chip" style={{ height: 20, fontSize: 10, padding: '0 8px' }}>
                      {t('participants.leader')}
                    </span>
                  )}
                </div>
                {m.role && <div style={{ fontSize: 12, color: COLORS.outline }}>{roleLabel(m.role)}</div>}
              </div>
              <input
                type="checkbox"
                checked={checked}
                disabled={isLeader}
                onChange={() => toggle(m.id)}
                aria-label={m.name}
                title={isLeader ? t('participants.leadersDefault') : undefined}
                className="wh-checkbox"
              />
            </label>
          )
        })}
        {filtered.length === 0 && (
          <div style={{ padding: 32, textAlign: 'center', color: COLORS.outline, fontSize: 13 }}>
            {t('participants.noMatch', { query })}
          </div>
        )}
      </div>

      {onAddGuest && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <button
            type="button"
            className="wh-btn wh-btn-outline"
            style={{ width: '100%', height: 44, fontSize: 14 }}
            onClick={() => setInviteOpen(true)}
          >
            {t('participants.guest.addButton')}
          </button>

          {guests && guests.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <span style={{ fontSize: 12, color: COLORS.outline, fontWeight: 600 }}>
                {t('participants.guest.listHeading')}
              </span>
              {guests.map((g, i) => (
                <div
                  key={`${g.channel}:${g.value}`}
                  className="wh-card"
                  style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px' }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: COLORS.onSurface, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {g.value}
                    </div>
                    <div style={{ fontSize: 12, color: COLORS.outline }}>{t('participants.guest.pendingNote')}</div>
                  </div>
                  {onRemoveGuest && (
                    <button
                      type="button"
                      className="wh-btn wh-btn-text"
                      aria-label={t('participants.guest.remove')}
                      onClick={() => onRemoveGuest(i)}
                      style={{ minWidth: 0, padding: '0 10px', fontSize: 18, lineHeight: 1 }}
                    >
                      ×
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}

          {inviteOpen && (
            <InviteByEmailOrPhoneModal
              labels={inviteLabels}
              onInvite={onAddGuest}
              onClose={() => setInviteOpen(false)}
            />
          )}
        </div>
      )}
    </div>
  )
}
